import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { ArrowLeft, Package, MapPin, FileText } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import SettlementTimeline from '../../components/SettlementTimeline';

export default function SettlementOrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: orderData, isLoading } = useQuery({
    queryKey: ['vendor', 'settlement-order', id],
    queryFn: async () => {
      const response = await vendorApi.getSettlementOrderDetails(id!);
      return response.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01') return '-';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const formatCurrency = (amount: number) => {
    return amount?.toLocaleString('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 2,
    }) || '0 SEK';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar orderdetaljer...</p>
        </div>
      </div>
    );
  }

  const order = orderData?.order;
  const lines = orderData?.documentLines || [];

  // The settlement pays the line Amount; commission is deducted per line
  const totalAmount = lines.reduce((sum: number, line: any) => sum + (line.amount || 0), 0);
  const totalCommission = lines.reduce((sum: number, line: any) => sum + (line.commissionAmount || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tillbaka
          </button>

          {/* Order Header */}
          <div
            className="panel mb-6"
          >
            <div className="bg-surface p-6">
              <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">
                  Order {order?.no}
                </h1>
                <p className="text-gray-600 mt-1">Order-ID: {order?.orderId}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Orderdatum</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(order?.orderDate)}</p>
              </div>
            </div>

            {/* Order Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {/* Customer Info */}
              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Kundinformation</h3>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Kundnr:</span> {order?.customerNo}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Namn:</span> {order?.name}
                  </p>
                  {order?.customerReference && (
                    <p className="text-sm">
                      <span className="font-medium">Referens:</span> {order?.customerReference}
                    </p>
                  )}
                </div>
              </div>

              {/* Resource Info */}
              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Resursinformation</h3>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Resursnr:</span> {order?.resourceNo}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Text:</span> {order?.resourceText}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Typ:</span> {order?.resourceTypeName}
                  </p>
                </div>
              </div>

              {/* External Info */}
              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Extern information</h3>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Dokumentnr:</span> {order?.externalDocumentNo}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Ordertyp:</span>{' '}
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {order?.externalOrderType}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>

          {order && <SettlementTimeline order={order} settlementDocument={orderData?.settlementDocument} />}

          {/* Ship From / Ship To */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Ship From */}
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-blue-600" />
                <h2 className="text-[15px] font-semibold text-ink">Skicka från</h2>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">{order?.shipFromName || '-'}</p>
                {order?.shipFromName2 && <p className="text-sm">{order.shipFromName2}</p>}
                {order?.shipFromAddress && <p className="text-sm">{order.shipFromAddress}</p>}
                {order?.shipFromAddress2 && <p className="text-sm">{order.shipFromAddress2}</p>}
                <p className="text-sm">
                  {order?.shipFromPostCode} {order?.shipFromCity}
                </p>
                {order?.shipFromCountryRegionCode && (
                  <p className="text-sm text-gray-600">{order.shipFromCountryRegionCode}</p>
                )}
              </div>
            </div>

            {/* Ship To */}
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-green-600" />
                <h2 className="text-[15px] font-semibold text-ink">Skicka till</h2>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">{order?.shipToName || '-'}</p>
                {order?.shipToName2 && <p className="text-sm">{order.shipToName2}</p>}
                {order?.shipToAddress && <p className="text-sm">{order.shipToAddress}</p>}
                {order?.shipToAddress2 && <p className="text-sm">{order.shipToAddress2}</p>}
                <p className="text-sm">
                  {order?.shipToPostCode} {order?.shipToCity}
                </p>
                {order?.shipToCountryRegionCode && (
                  <p className="text-sm text-gray-600">{order.shipToCountryRegionCode}</p>
                )}
              </div>
            </div>
          </div>

          {/* Document Lines */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h2 className="text-[15px] font-semibold text-ink">Dokumentrader</h2>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Belopp att avräkna</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
                {totalCommission !== 0 && (
                  <p className="mt-0.5 text-xs text-gray-500">varav provision −{formatCurrency(totalCommission)}</p>
                )}
              </div>
            </div>

            {lines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Radnr
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Typ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nr
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Beskrivning
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antal
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        À-pris
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provision
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Belopp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lines.map((line: any, index: number) => (
                      <tr key={line.systemId || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{line.lineNo}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{line.lineType}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{line.no}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">{line.description}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm text-gray-900">{line.quantity || 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm text-gray-900">{formatCurrency(line.unitPrice || 0)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm text-gray-500">
                            {line.commissionAmount ? `−${formatCurrency(line.commissionAmount)} (${line.commission || 0} %)` : '–'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(line.amount || 0)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Inga dokumentrader tillgängliga
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
